/**
 * Subscription Plans Data
 * Centralized subscription plan definitions and utilities
 */

import type { SubscriptionTier, SubscriptionPlan, PlanFeatures } from '../types/subscription';

// Subscription plan definitions
export const SUBSCRIPTION_PLANS: Record<SubscriptionTier, SubscriptionPlan> = {
  free: {
    id: 'free',
    name: 'Free',
    description: 'Perfect for getting started with basic fire alarm calculations',
    monthlyPrice: 0,
    annualPrice: 0,
    cta: 'Get Started',
    features: {
      maxProjects: 3,
      maxCalculations: 10,
      pdfExports: false,
      brandedPdfs: false,
      nfpaReferences: false,
      teamCollaboration: false,
      prioritySupport: false,
      apiAccess: false,
      customBranding: false,
      ssoIntegration: false,
      dedicatedSupport: false
    }
  },
  starter: {
    id: 'starter',
    name: 'Starter',
    description: 'For individual professionals who need more capacity',
    monthlyPrice: 29,
    annualPrice: 290,
    cta: 'Start Free Trial',
    features: {
      maxProjects: 10,
      maxCalculations: 100,
      pdfExports: true,
      brandedPdfs: false,
      nfpaReferences: true,
      teamCollaboration: false,
      prioritySupport: false,
      apiAccess: false,
      customBranding: false,
      ssoIntegration: false,
      dedicatedSupport: false
    }
  },
  pro: {
    id: 'pro',
    name: 'Professional',
    description: 'For teams and businesses with advanced needs',
    monthlyPrice: 79,
    annualPrice: 790,
    popular: true,
    cta: 'Start Free Trial',
    features: {
      maxProjects: 'unlimited',
      maxCalculations: 'unlimited',
      pdfExports: true,
      brandedPdfs: true,
      nfpaReferences: true,
      teamCollaboration: true,
      prioritySupport: true,
      apiAccess: false,
      customBranding: false,
      ssoIntegration: false,
      dedicatedSupport: false
    }
  },
  enterprise: {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'For large organizations with custom requirements',
    monthlyPrice: 0,
    annualPrice: 0,
    cta: 'Contact Sales',
    features: {
      maxProjects: 'unlimited',
      maxCalculations: 'unlimited',
      pdfExports: true,
      brandedPdfs: true,
      nfpaReferences: true,
      teamCollaboration: true,
      prioritySupport: true,
      apiAccess: true,
      customBranding: true,
      ssoIntegration: true,
      dedicatedSupport: true
    }
  }
};

// Usage limits for tracking (separate from feature flags)
export interface UsageLimits {
  projectsCreated: number;
  calculationsRun: number;
  pdfExportsCount: number;
  resourceDownloads: number;
}

// Subscription limits for feature gating
export const SUBSCRIPTION_LIMITS: Record<SubscriptionTier, PlanFeatures & UsageLimits> = {
  free: {
    ...SUBSCRIPTION_PLANS.free.features,
    projectsCreated: 3,
    calculationsRun: 10,
    pdfExportsCount: 0,
    resourceDownloads: 5
  },
  starter: {
    ...SUBSCRIPTION_PLANS.starter.features,
    projectsCreated: 10,
    calculationsRun: 100,
    pdfExportsCount: 20,
    resourceDownloads: 50
  },
  pro: {
    ...SUBSCRIPTION_PLANS.pro.features,
    projectsCreated: -1, // unlimited
    calculationsRun: -1,
    pdfExportsCount: -1,
    resourceDownloads: -1
  },
  enterprise: {
    ...SUBSCRIPTION_PLANS.enterprise.features,
    projectsCreated: -1,
    calculationsRun: -1,
    pdfExportsCount: -1,
    resourceDownloads: -1
  }
};

// Tier hierarchy for comparison
const TIER_HIERARCHY: SubscriptionTier[] = ['free', 'starter', 'pro', 'enterprise'];

/**
 * Check if a tier is higher or equal to another tier
 */
export function isTierHigherOrEqual(currentTier: SubscriptionTier, requiredTier: SubscriptionTier): boolean {
  const currentIndex = TIER_HIERARCHY.indexOf(currentTier);
  const requiredIndex = TIER_HIERARCHY.indexOf(requiredTier);
  return currentIndex >= requiredIndex;
}

/**
 * Check if user can access a specific feature based on their tier
 */
export function canAccessFeature(
  tier: SubscriptionTier, 
  feature: keyof PlanFeatures
): boolean {
  const plan = SUBSCRIPTION_PLANS[tier];
  if (!plan) return false;
  
  const featureValue = plan.features[feature];
  
  // Boolean features
  if (typeof featureValue === 'boolean') {
    return featureValue;
  }
  
  // Numeric or unlimited features
  if (featureValue === 'unlimited' || (typeof featureValue === 'number' && featureValue > 0)) {
    return true;
  }
  
  return false;
}

/**
 * Get monthly price for a tier
 */
export function getMonthlyPrice(tier: SubscriptionTier): number {
  return SUBSCRIPTION_PLANS[tier]?.monthlyPrice ?? 0;
}

/**
 * Get annual price for a tier
 */
export function getAnnualPrice(tier: SubscriptionTier): number {
  return SUBSCRIPTION_PLANS[tier]?.annualPrice ?? 0;
}

/**
 * Get annual savings percentage compared to monthly billing
 */
export function getAnnualSavingsPercentage(tier: SubscriptionTier): number {
  const plan = SUBSCRIPTION_PLANS[tier];
  if (!plan || plan.monthlyPrice === 0) return 0;
  
  const monthlyTotal = plan.monthlyPrice * 12;
  const annualTotal = plan.annualPrice;
  
  if (monthlyTotal === 0) return 0;
  
  return Math.round(((monthlyTotal - annualTotal) / monthlyTotal) * 100);
}

/**
 * Get all features as a flat list for display
 */
export function getPlanFeaturesList(tier: SubscriptionTier): string[] {
  const features = SUBSCRIPTION_PLANS[tier]?.features;
  if (!features) return [];
  
  const featureLabels: Record<keyof PlanFeatures, string> = {
    maxProjects: 'Projects',
    maxCalculations: 'Calculations',
    pdfExports: 'PDF Exports',
    brandedPdfs: 'Branded PDFs',
    nfpaReferences: 'NFPA References',
    teamCollaboration: 'Team Collaboration',
    prioritySupport: 'Priority Support',
    apiAccess: 'API Access',
    customBranding: 'Custom Branding',
    ssoIntegration: 'SSO Integration',
    dedicatedSupport: 'Dedicated Support'
  };
  
  return Object.entries(features)
    .filter(([_, value]) => value === true || value === 'unlimited' || (typeof value === 'number' && value > 0))
    .map(([key, value]) => {
      const label = featureLabels[key as keyof PlanFeatures];
      if (value === 'unlimited') return `Unlimited ${label}`;
      if (typeof value === 'number') return `${value} ${label}`;
      return label;
    });
}
